import { Caption, Image, useStyles } from '@archipro-design/aria';
import { Link, useSearchParams } from 'react-router';
import * as S from './LoginPage.style';
import type { LoginSignUpProps } from '../component/login-component/LoginSignUp';
import LoginSignUp from '../component/login-component/LoginSignUp';
import { PolicyLink } from '../component/policy-link/PolicyLink';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { LogoAnimated } from '../../../../../rocco/src/components/logo-animated/LogoAnimated';
import { decodeLoginRedirect, useFeature } from '~/modules/root';
import { AuthPageCard } from '~/modules-rocco/user/component/auth-modal/AuthPageCard';

interface LoginPageProps {
    /**
     * Optional because the `/login` route renders this as its default export,
     * with no props at all. Only the 401 page supplies anything.
     */
    login?: Partial<LoginSignUpProps>;
}

export const LoginPage = (props?: LoginPageProps) => {
    const config = useConfigContext();
    const { css } = useStyles();
    const newAuthEnabled = useFeature('auth_modal_2026');

    const [searchParams] = useSearchParams();

    const skipPostSignUpRedirect =
        !!props?.login?.skipPostSignUpRedirect ||
        searchParams.get('skipPostSignUp') === '1';

    const redirect = decodeLoginRedirect(searchParams.get('redirect'));

    return (
        <div className={css(S.PageWrapper)}>
            <Image
                src={`${config.assetsBaseUrl}/assets/website/images/ui/home-hero-image-mini.png`}
                width={1920}
                height={1080}
                layout="fill"
                alt="login"
                objectFit="cover"
            />
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    width: '100%',
                    height: '100%',
                }}
            ></div>
            <div className={css(S.ContentWrap)}>
                {newAuthEnabled ? (
                    // The card carries its own brand pane with the ArchiPro
                    // mark, so the standalone logo would be the second one on
                    // screen. The backdrop and the policy row stay: they are the
                    // page's identity, and only the body was ever a duplicate of
                    // the modal.
                    <div className="flex min-h-0 w-full flex-1 items-center justify-center p-4 md:p-6">
                        <AuthPageCard
                            authSource="loginPage"
                            redirect={redirect}
                            skipPostSignUp={skipPostSignUpRedirect}
                            // The 401 page states why the reader was sent
                            // here. `subtitle`, not `message`: message only
                            // renders on the details step, so the reason would
                            // be invisible on the screen they actually land on.
                            // As the subtitle it shows on both, because the
                            // details step falls back to it.
                            subtitle={props?.login?.welcomeMessage || undefined}
                            defaultPublicUser={props?.login?.defaultPublicUser}
                            // The brand pane only appears at `md`, so without a
                            // cap the card below that is a full-width white band
                            // around a 363px column. The modal has the same
                            // stretch but earns it by being full-screen; a page
                            // card just looks stranded.
                            className="max-md:max-w-135"
                        />
                    </div>
                ) : (
                    <div className={css(S.Content)}>
                        <Link className={css(S.LogoWrapper)} to="/">
                            <LogoAnimated
                                size={4}
                                mode="disabled"
                                color="light"
                            />
                        </Link>

                        <div className={css(S.LoginWrapper)}>
                            <LoginSignUp
                                {...props?.login}
                                authSource="loginPage"
                                skipPostSignUpRedirect={skipPostSignUpRedirect}
                                redirect={redirect}
                            />
                        </div>
                    </div>
                )}

                <div className={css(S.Links)}>
                    <PolicyLink as="a" />
                    <Caption variant="02" as={Link} to="/become-a-pro">
                        ArchiPro for Business
                    </Caption>
                    <Caption variant="02" as="a" href="/contact-us">
                        Contact Us
                    </Caption>
                </div>
            </div>
        </div>
    );
};
